export enum UserRole {
  MASTER_ADMIN = 'MASTER_ADMIN',
  SUPER_ADMIN = 'SUPER_ADMIN',
  CENTRAL_ADMIN = 'CENTRAL_ADMIN',
  FINANCE = 'FINANCE',
  ACADEMIC = 'ACADEMIC',
  TENTOR = 'TENTOR',
  STUDENT = 'STUDENT',
}

export enum GradeLevel {
  SD_5 = 'SD Kelas 5',
  SD_6 = 'SD Kelas 6',
  SMP_7 = 'SMP Kelas 7',
  SMP_8 = 'SMP Kelas 8',
  SMP_9 = 'SMP Kelas 9',
  SMA_10 = 'SMA Kelas 10',
  SMA_11_IPA = 'SMA Kelas 11 IPA',
  SMA_11_IPS = 'SMA Kelas 11 IPS',
  SMA_12_IPA = 'SMA Kelas 12 IPA',
  SMA_12_IPS = 'SMA Kelas 12 IPS',
  ALUMNI = 'ALUMNI (GAP YEAR)',
}

export enum EmployeeRole {
  STAFF = 'Staff',
  TENTOR = 'Tentor',
  OB = 'OB',
  SECURITY = 'Security',
  OTHER = 'Lainnya',
}

export enum ExpenseCategory {
  OPERATIONAL = 'Operasional',
  SALARY_STAFF = 'Gaji Karyawan',
  HONOR_TEACHER = 'Honor Tentor',
  ASSET = 'Pembelian Aset',
  OTHER = 'Lainnya',
}

export interface Profile {
  id: string;
  name: string;
  role: UserRole;
  branch_id: string | null;
  created_at?: string;
  email?: string;
}

export interface Branch {
  id: string;
  name: string;
  address: string | null;
  is_active?: boolean;
}

export interface GradeLevelEntity {
  id: string;
  level: string;
}

export interface Major {
  id: string;
  code: string;
  name: string;
}

export interface ClassRoom {
  id: string;
  branch_id: string;
  grade_level_id: string;
  major_id?: string | null;
  parallel?: number | null;
  name: string;
  max_capacity?: number; // <--- Menambahkan properti max_capacity
  created_at?: string;
  grade_level?: GradeLevelEntity;
  major?: Major;
}

export interface Tentor {
  id: string;
  user_id: string;
  name?: string;
  email?: string;
  branch_id: string;
  subject: string;
  base_salary: number;
  is_active: boolean;
  created_at?: string;
}

export interface Employee {
  id: string;
  name: string;
  role: EmployeeRole;
  branch_id: string;
  base_salary: number;
  phone_number?: string;
  bank_account?: string;
  created_at?: string;
}

export interface InstallmentItem {
  label: string;
  amount: number;
  status: 'UNPAID' | 'PAID' | 'PENDING';
  due_date?: string | null;
}

export interface Student {
  id: string;
  user_id: string;
  name: string;
  grade: string;
  class_id?: string | null;
  branch_id: string;
  phone_number?: string;
  email?: string;
  subjects?: string[];
  notes?: string;
  financial_record?: {
    total_cost: number;
    registration_fee: number;
    payment_type: 'CASH' | 'ANGSUR';
    installment_count: number;
    payments: any[];
    installment_plan: InstallmentItem[];
  };
  created_at?: string;
  joined_at?: string;
  parent_name?: string;
  parent_phone?: string;
  program_choice?: string;
  address?: string;
  school_origin?: string;
  class_room?: ClassRoom;
  is_active?: boolean;
  status?: string;
}

export interface AttendanceRecord {
  id?: string;
  student_id: string;
  date: string;
  status: 'H' | 'I' | 'S' | 'A';
  branch_id: string;
  created_at?: string;
}

export interface Schedule {
  id: string;
  branch_id: string;
  class_id: string;
  day: string;
  time: string;
  subject: string;
  grade: string;
  teacher_id?: string | null;
  teacher_user_id?: string;
  teacher_name?: string;
  teacher?: string;
  room?: string;
  date?: string;
  tentor_material_url?: string;
  teacher_material_url?: string;
  student_material_url?: string;
  class_room?: ClassRoom;
}

export type ScheduleItem = Schedule;

export interface LearningModule {
  id: string;
  title: string;
  description: string | null;
  grade: string;
  branch_id: string;
  file_url: string | null;
  file_path?: string; // Menyimpan path internal storage
  subject: string;
  created_at?: string;
  file_name?: string;
}

export interface LearningFolder {
  id: string;
  title: string;
  grade: string;
  subject: string;
  folder_id: string;
  folder_url: string;
  branch_id: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ExamQuestion {
  id?: string;
  question: string;
  image_url?: string;
  options: string[];
  correctAnswer: string;
}

export interface Exam {
  id: string;
  branch_id: string;
  class_id?: string;
  subject: string;
  grade: string;
  topic: string;
  questions: ExamQuestion[];
  date: string;
  start_time?: string;
  duration: number;
  status?: 'DRAFT' | 'PUBLISHED';
  results_publish_status?: 'IMMEDIATE' | 'SCHEDULED';
  results_published_at?: string;
}

export type ExamItem = Exam;

export interface ExamResult {
  id: string;
  exam_id: string;
  student_id: string;
  student_name: string;
  score: number;
  total_questions: number;
  correct_answers: number;
  answers: any;
  submitted_at: string;
}

export interface PendingPayment {
  id: string;
  student_id: string;
  student_name: string;
  branch_id: string;
  amount: number;
  date: string;
  method: string;
  notes: string;
  transfer_proof_path?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'VERIFIED';
  installment_index?: number | null;
}

export interface OnlineRegistration {
  id: string;
  branch_id: string;
  name: string;
  grade: string;
  phone_number: string;
  total_cost: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'VERIFIED';
  program_choice: string;
  transfer_proof_path?: string;
  submitted_at: string;
  address?: string;
  school_origin?: string;
  parent_name?: string;
  parent_phone?: string;
  payment_method?: 'CASH' | 'ANGSUR';
  installment_count?: number;
}

export interface AIAnalysisResult {
  summary: string;
  strengths: string[];
  weaknesses: string[];
  recommendedFocus: string[];
  studyPlan: {
    week: number;
    topic: string;
    activity: string;
  }[];
}

export interface DailyReport {
  id: string;
  student_id: string;
  tentor_id: string;
  date: string;
  subject: string;
  attendance: 'H' | 'I' | 'S' | 'A';
  comprehension_score: number;
  notes: string;
  created_at: string;
}

export interface CMSSection {
  id?: string;
  page_id: string;
  type: string;
  content: any;
  position: number;
  is_active: boolean;
}

export interface LandingPageConfig {
  brand_name: string;
  brand_motto: string;
  logo_url: string;
  hero_title: string;
  hero_subtitle: string;
  hero_image_url: string;
  about_title: string;
  about_description: string;
  features: { title: string; description: string }[];
  activities: any[];
  public_profiles: any[];
  tips_and_tricks: any[];
  promos: any[];
}

export interface BranchIncomeRecord {
  id: string;
  branch_id: string;
  date: string;
  accounting_period?: string;
  description: string;
  amount: number;
}

export interface ExpenseRecord {
  id: string;
  branch_id: string;
  date: string;
  accounting_period?: string;
  category: ExpenseCategory;
  description: string;
  amount: number;
  recipient?: string;
}

export interface ProgramPrice {
  program_name: string;
  grade: string;
  price: number;
  cash_price: number;
  installment_price: number;
  installment_durations: number[];
  registration_fee: number;
  branch_id: string;
}

export interface DiscountVoucher {
  id: string;
  branch_id: string;
  code: string;
  discount_percent: number;
  target_program: string;
  is_active: boolean;
}

export interface BankInfo {
  bankName: string;
  accountNumber: string;
  accountHolder: string;
}

export interface PayrollRun {
  id: string;
  branch_id: string;
  period: string;
  cutoff_date: string;
  generated_at: string;
}

export interface TeachingSession {
  id: string;
  tentor_id: string;
  period: string;
  session_count: number;
  honor_per_session: number;
  is_verified: boolean;
  branch_id: string;
}
